import React from "react";
import classNames from "classnames";
import {
  TertiaryButton,
  UnderlinedButton,
} from "@JohnLewisPartnership/jl-design-system/dist/elements/button/Button";
import { Pin24PxOutlinedIcon } from "@JohnLewisPartnership/jl-design-system/dist/elements/icons-jb/Pin24PxOutlined";
import { ProductType } from "../../types/types";
import styles from "./RetiredTopInfoBox.module.scss";
import { PRODUCT_DESCRIPTION_ACCORDION_INSTANCE_NAME } from "../ProductAccordion/components/ProductDescriptionAccordion/ProductDescriptionAccordionUI";

type Props = {
  /**
   * A string representing the title of the product. It can also be undefined.
   */
  title: string | undefined;
  /**
   * A string representing the primary image URL of the product.
   */
  primaryImageUrl: string;
  /**
   * The type of the product. It can also be undefined.
   */
  productType: ProductType | undefined;
};

export function RetiredTopInfoBoxUI({
  title,
  primaryImageUrl,
  productType,
}: Props) {
  return (
    <div className={styles["top-info-box"]} data-testid="RetiredTopInfoBox">
      <div className={styles["top-info-box__image"]}>
        <img alt={title} src={`${primaryImageUrl}?$rsp-pdp-port-320$`} />
      </div>
      <div className={styles["top-info-box__info"]}>
        <h2 className={styles["top-info-box__title"]}>
          No longer available online
        </h2>
        <p className={styles["top-info-box__desc"]}>{title}</p>
        <ul className={styles["top-info-box__action-list"]}>
          {productType !== "RetiredStaticBundle" && (
            <li className={styles["top-info-box__action"]}>
              <UnderlinedButton
                tag="a"
                data-testid="skiplinks:top-box-description"
                href="#accordion-description"
                onClick={(event: React.MouseEvent) => {
                  event.preventDefault();
                  scrollToDescription();
                }}
              >
                View product description
              </UnderlinedButton>
            </li>
          )}

          {productType === "RetiredStaticBundle" && (
            <li className={styles["top-info-box__action"]}>
              <UnderlinedButton
                tag="a"
                data-testid="skiplinks:top-box-howToReturn"
                href="/customer-services/returns/how-do-i-return-an-item"
              >
                How to return an item
              </UnderlinedButton>
            </li>
          )}

          {productType !== "RetiredStaticBundle" && (
            <li className={styles["top-info-box__action"]}>
              <TertiaryButton
                className={classNames(styles["top-info-box__button"])}
                tag="a"
                small
                data-testid="skiplinks:top-box-store-stock"
                href="#store-stock-button-wrapper"
                onClick={(event: React.MouseEvent) => {
                  event.preventDefault();
                  scrollToStoreStock();
                }}
                IconComponent={Pin24PxOutlinedIcon}
              >
                Check in-store stock
              </TertiaryButton>
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}

function scrollToDescription() {
  fireOpenProductDescriptionEvent();
  scrollTo("#accordion-description");
}

function scrollToStoreStock() {
  scrollTo("#store-stock-button-wrapper");
}

function scrollTo(targetId: string) {
  const scroll = () =>
    document
      .querySelector(targetId)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });

  setTimeout(scroll, 0);
}

function fireOpenProductDescriptionEvent() {
  const event = new CustomEvent(
    `jlds:accordion:${PRODUCT_DESCRIPTION_ACCORDION_INSTANCE_NAME}`,
    {
      detail: { action: "open" },
    },
  );
  document.body.dispatchEvent(event);
}
